import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FadeUp } from "@/components/landing/Reveal";
import { api, canUse, RISK } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const riskFor = (s) => (s >= 80 ? "Bajo" : s >= 60 ? "Medio" : "Alto");

const Gauge = ({ value, color, testid }) => {
  const L = Math.PI * 80;
  return (
    <div className="relative w-full max-w-[240px] aspect-[2/1.15]" data-testid={testid}>
      <svg viewBox="0 0 200 115" className="w-full h-full">
        <path d="M20 100 A80 80 0 0 1 180 100" fill="none" stroke="rgba(245,241,232,0.08)" strokeWidth="14" strokeLinecap="round" />
        {value != null && (
          <motion.path d="M20 100 A80 80 0 0 1 180 100" fill="none" stroke={color} strokeWidth="14" strokeLinecap="round" strokeDasharray={L}
            initial={{ strokeDashoffset: L }} animate={{ strokeDashoffset: L * (1 - value / 100) }} transition={{ duration: 1.5, ease: [0.22, 1, 0.36, 1], delay: 0.2 }} />
        )}
      </svg>
      <div className="absolute inset-x-0 bottom-0 text-center">
        <div className="font-display-x text-4xl sm:text-5xl font-black tracking-tighter">{value == null ? <span className="font-display text-xl text-[#F5F1E8]/50 tracking-normal">Sin datos</span> : <>{value}<span className="text-2xl" style={{ color }}>%</span></>}</div>
      </div>
    </div>
  );
};

const PillarCard = ({ pillar, modules, audits, delay }) => {
  const latest = modules.map((m) => ({ m, a: audits.find((a) => a.module_id === m.id) }));
  const done = latest.filter((x) => x.a);
  const avg = done.length ? Math.round((done.reduce((n, x) => n + x.a.score, 0) / done.length) * 10) / 10 : null;
  const r = avg == null ? null : RISK[riskFor(avg)];
  const tid = `pillar-overview-${pillar.id}`;
  return (
    <FadeUp delay={delay} className="border border-white/10 bg-[#111722] p-6 sm:p-8 flex flex-col gap-6" data-testid={tid}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#F5F1E8]/60">Pilar {pillar.id === "stps" ? "I" : "II"}</div>
          <h3 className="font-display mt-2 text-xl sm:text-2xl font-bold leading-tight">{pillar.title}</h3>
        </div>
        {r ? (
          <span className={`shrink-0 px-3 py-1 rounded-full border text-xs font-semibold ${r.bg} ${r.border} ${r.text}`} data-testid={`${tid}-risk`}>{r.label}</span>
        ) : (
          <span className="shrink-0 px-3 py-1 rounded-full border border-white/15 text-xs text-[#F5F1E8]/65">Sin evaluar</span>
        )}
      </div>
      <div className="flex flex-col sm:flex-row sm:items-end gap-6">
        <Gauge value={avg} color={r?.color} testid={`${tid}-gauge`} />
        <div className="flex-1 min-w-0 space-y-3">
          {latest.map(({ m, a }) => (
            <div key={m.id} data-testid={`${tid}-module-${m.id}`}>
              <div className="flex justify-between gap-3 text-sm"><span className="truncate">{m.name}</span><span className="font-mono shrink-0">{a ? `${a.score}%` : "—"}</span></div>
              <div className="mt-1 h-1 bg-white/5"><div className="h-full transition-[width] duration-700" style={{ width: `${a?.score ?? 0}%`, background: a ? RISK[a.risk]?.color : "transparent" }} /></div>
            </div>
          ))}
        </div>
      </div>
      <p className="text-xs text-[#F5F1E8]/60 font-mono">
        {modules.length === 0 ? "Módulos disponibles desde el Plan Base" : <>{done.length} de {modules.length} {modules.length === 1 ? "módulo evaluado" : "módulos evaluados"} · promedio de la auditoría más reciente de cada módulo</>}
      </p>
      {done.length < modules.length && (
        <Link to={`/auditoria?modulo=${latest.find((x) => !x.a)?.m.id}`} data-testid={`${tid}-next-link`} className="text-sm font-semibold text-[#F59E0B] hover:text-[#F5F1E8] transition-colors">
          Evaluar {latest.find((x) => !x.a)?.m.name} →
        </Link>
      )}
    </FadeUp>
  );
};

export const ComplianceOverview = ({ audits }) => {
  const [data, setData] = useState({ pillars: [], modules: [] });
  const { user } = useAuth();
  useEffect(() => {
    api.get("/modules").then((r) => setData({ pillars: r.data?.pillars ?? [], modules: Array.isArray(r.data?.modules) ? r.data.modules : [] })).catch(() => {});
  }, []);
  const sorted = useMemo(() => [...audits].sort((a, b) => b.created_at.localeCompare(a.created_at)), [audits]);
  if (!data.pillars.length) return null;
  return (
    <section className="mt-14" data-testid="compliance-overview">
      <div className="font-mono text-xs uppercase tracking-[0.22em] text-[#F59E0B]">Estado de cumplimiento</div>
      <div className="mt-6 grid lg:grid-cols-2 gap-4">
        {data.pillars.map((p, i) => (
          <PillarCard key={p.id} pillar={p} modules={data.modules.filter((m) => m.pillar === p.id && (canUse(user, m.level) || audits.some((a) => a.module_id === m.id)))} audits={sorted} delay={i * 0.08} />
        ))}
      </div>
    </section>
  );
};
